/**
 * PeopleSearchResults
 *
 * Lists people results and opens a bottom Drawer with a user-specific objectives
 * view when an item is clicked. Maintains minimal internal state for the selected
 * person and drawer visibility.
 *
 * Props
 * - results: Array of people to list.
 *
 * Notes
 * - Drawer onClose ignores backdrop and Escape to avoid accidental dismissal
 *   while interacting with nested content.
 */
import React from "react";
import {
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
} from "@mui/material";
import "./people-search-results.scss";

interface Person {
  id: number;
  name: string;
  avatar: string;
}

interface PeopleSearchResultsProps {
  results: Person[];
  setIsShowUserObjectiveDrawer?: (value: boolean) => void;
  setSelectedApprover?: (value: any) => void;
}

const PeopleSearchResults: React.FC<PeopleSearchResultsProps> = ({
  results,
  setIsShowUserObjectiveDrawer: externalSetDrawerState,
  setSelectedApprover: externalSetSelectedApprover,
}) => {
  // Use external state if provided, otherwise use internal state
  // Use external state if provided, otherwise use internal state

  const setIsShowUserObjectiveDrawer = externalSetDrawerState ?? (() => {});
  const setSelectedApprover = externalSetSelectedApprover ?? (() => {});

  if (results.length === 0) {
    return <div className="no-results">No people found.</div>;
  }

  return (
    <List className="people-search-results scrollable">
      {results.map((person) => (
        <ListItem
          key={person.id}
          sx={{ cursor: "pointer" }}
          onClick={() => {
            setSelectedApprover({ id: person.id, name: person.name });
            setIsShowUserObjectiveDrawer(true);
          }}
        >
          <ListItemAvatar>
            <Avatar src={person.avatar} alt={person.name} />
          </ListItemAvatar>
          <ListItemText primary={person.name} />
        </ListItem>
      ))}
    </List>
  );
};

export default React.memo(PeopleSearchResults, (prevProps, nextProps) => {
  // Only re-render if results actually changed (deep comparison of IDs)
  if (prevProps.results.length !== nextProps.results.length) {
    return false; // Re-render
  }

  // Check if the actual IDs changed
  const prevIds = prevProps.results.map((r) => r.id).join(",");
  const nextIds = nextProps.results.map((r) => r.id).join(",");

  if (prevIds !== nextIds) {
    return false; // Re-render
  }

  return true; // Skip re-render
});
